import { media, useMatchMedia } from '@rst/libs-ts'
import {
	cssTextHeader2,
	cssTextRegular14,
	IconNameType,
	RstIcon,
	Text,
} from '@rst/ui-ts'
import React from 'react'
import styled, { css } from 'styled-components'
import { color, getLeftRightRtlCss, typography, Button2 } from '../../atoms'
import { HeaderPublicPropsType } from '../type'

export interface HeaderPropsType extends HeaderPublicPropsType {
	isMobileFullScreen?: boolean
	close?: () => void
}

export const Header: React.FC<HeaderPropsType> = ({
	title,
	subTitle,
	iconTitle,
	isMobileFullScreen,
	customComponent,
	close,
}) => {
	const sm = useMatchMedia(x => x.less.sm)

	if (typeof customComponent === 'function') return customComponent(close)

	if (customComponent) return customComponent

	if (sm && isMobileFullScreen)
		return (
			<WrapperLine2 className="awesome-modal-wrapper-header-full-screen" small>
				<Arrow
					className="awesome-modal-header-close"
					onClick={close}
					type={'rst-arrows_left' as IconNameType}
				/>
				<div>
					<Title small>
						{iconTitle ? <RstIcon type={iconTitle} /> : null}
						{title}
					</Title>
					{subTitle ? <SubTitle>{subTitle}</SubTitle> : null}
				</div>
			</WrapperLine2>
		)

	return (
		<>
			<HeaderTitleClose title={title} subTitle={subTitle} close={close} />
		</>
	)
}

export const HeaderTitleClose = ({ title, subTitle, close }) => {
	return (
		<Wrapper className="awesome-modal-wrapper-header-close">
			<div>
				{title && <DesktopTitle>{title}</DesktopTitle>}
				{subTitle && <DesktopSubTitle>{subTitle}</DesktopSubTitle>}
			</div>
			<Icon onClick={close} />
		</Wrapper>
	)
}

const DesktopTitle = styled(Text)`
	${typography.head3.semiBold};

	${media.pure.less(media.size.sm)} {
		${typography.head4.bold};
	}
`

const Wrapper = styled.div`
	display: flex;
	width: 100%;
	min-height: 76px;
	padding: 27.5px 0px 19.5px;
	justify-content: space-between;

	${DesktopTitle} {
		padding-right: 40px;
	}

	${media.pure.less(media.size.sm)} {
		padding: 16px 0px;
		height: 56px;
	}

	div {
		display: flex;
		flex-direction: column;
	}
`

export { DesktopTitle as Title }

const SubTitle = styled(Text)`
	${cssTextRegular14};
	line-height: 160%;
	color: ${color('gray-15')};
	display: block;
`

const Title = styled(Text)`
	${cssTextHeader2};
	font-size: 27px;
	line-height: 130%;
	display: flex;
	align-items: center;
	color: ${color('gray-20')};
	& > i {
		font-size: 24px;
		width: 32px;
		height: 32px;
		margin-right: 5px;
		display: flex;
		justify-content: center;
		align-items: center;
	}
	& + ${SubTitle} {
		margin-top: 5px;
	}
	${({ small }) =>
		small &&
		css`
			font-size: 16px;
			line-height: 160%;
			& + ${SubTitle} {
				margin-top: 0;
			}
		`}
`

const Arrow = styled(RstIcon)`
	display: flex;
	justify-content: center;
	align-items: center;
	width: 40px;
	height: 40px;
	margin-right: 10px;
	font-size: 38px;
`

const WrapperLine2 = styled.div<{ small?: boolean }>`
	padding: 8px 0;
	margin-bottom: 16px;
	direction: ${({ theme }) => (theme.rtl ? 'rtl' : 'ltr')};
	${({ small }) =>
		small &&
		css`
			display: flex;
			align-items: center;
			position: sticky;
			top: 0;
			background: inherit;
			z-index: 10;
		`}
	${media.pure.less(media.size.sm)} {
		margin-bottom: 8px;
	}
`

export const Icon = styled(Button2).attrs({
	isTextBtn: true,
	icon: 'rst-close',
	className: 'awesome-modal-header-close',
})`
	position: absolute;
	top: 24px;
	right: 32px;
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 2px;
	width: 40px;
	height: 40px;
	font-size: 18px;
	border-radius: 8px;
	${media.pure.less(media.size.sm)} {
		top: 8px;
		right: 8px;
		font-size: 14px;
	}
	color: ${color('gray-20', { theme: false })} !important;
	&:hover {
		background-color: ${color('gray-07', { theme: false })};
	}
	&:focus {
		box-shadow: 0 0 0 4px ${color('primary-04', { theme: false })};
	}
`

const DesktopSubTitle = styled(Text)`
	${typography.subheadline.regular}

	color: ${color('gray-14')}
`
